import React, { useState, useMemo } from 'react';
import { Plus, Edit, Trash2, MapPin, Search } from 'lucide-react';
import { SearchBar } from '../shared/SearchBar';
import { SortableTableHeader } from '../shared/SortableTableHeader';
import { Pagination } from '../shared/Pagination';
import { FormModal } from '../shared/FormModal';
import { ConfirmModal } from '../../../framework/modals/ConfirmModal';
import { CustomerDropdown } from '../shared/CustomerDropdown';
import { FormInput } from '../shared/FormInput';
import { FormSelect } from '../shared/FormSelect';
import { useLocationManagement } from '../hooks/useLocationManagement';
import { useModeratorData } from '../hooks/useModeratorData';
import { useTableSort } from '../hooks/useTableSort';
import { useModeratorPermissions } from '../hooks/useModeratorPermissions';
import type { Customer } from '../types/moderator.types';

export const LocationsSection: React.FC = () => {
  const { locations, states, customers, refetchLocations, refetchModeratorData } = useModeratorData();
  const location = useLocationManagement(refetchLocations, refetchModeratorData);
  const { canAdd, canChange, canDelete } = useModeratorPermissions();
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const canCreateLocation = selectedCustomer
    ? canAdd(selectedCustomer.id)
    : customers.some((customer) => canAdd(customer.id));

  const customerOptions = useMemo(() => 
    customers.map(c => ({ value: c.id, label: c.name })),
    [customers]
  );

  const stateOptions = useMemo(() => 
    states.map(s => ({ value: s.id, label: `${s.name} (${s.code})` })),
    [states]
  );

  const filteredLocations = locations.filter(loc => {
    const matchesSearch = loc.name.toLowerCase().includes(location.searchTerm.toLowerCase());
    // If no customer selected, show all locations. Otherwise, filter by customer
    const matchesCustomer = !selectedCustomer || loc.customerId === selectedCustomer.id;
    return matchesSearch && matchesCustomer;
  });

  const {
    paginatedData,
    sortField,
    sortDirection,
    currentPage,
    totalPages,
    itemsPerPage,
    handleSort,
    setCurrentPage,
    setItemsPerPage
  } = useTableSort(filteredLocations, 'name', 10);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          Location Management
        </h2>
        {canCreateLocation && (
        <button
          onClick={() => {
            location.handleCreate(selectedCustomer?.id);
          }}
          className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors"
          style={{
            backgroundColor: 'var(--color-accent)',
            color: 'white'
          }}
        >
          <Plus className="w-4 h-4" />
          <span>Add Location</span>
        </button>
        )}
      </div>

      {/* Customer Selection */}
      <CustomerDropdown
        customers={customers}
        selectedCustomer={selectedCustomer}
        onSelectCustomer={(customer) => {
          setSelectedCustomer(customer);
          location.setSearchTerm('');
        }}
        helperText="Select your organization to access locations"
      />

      {/* Search */}
      <div 
        className="rounded-xl shadow-sm border p-6"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-sm)'
        }}
      >
        <div className="relative">
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
            <Search className="w-4 h-4 inline mr-2" />
            Search locations
          </label>
          <div className="relative">
      <SearchBar
        value={location.searchTerm}
        onChange={location.setSearchTerm}
              placeholder={selectedCustomer ? `Search locations for ${selectedCustomer.name}...` : "Search all locations..."}
      />
          </div>
          {selectedCustomer && (
            <p className="text-sm mt-2" style={{ color: 'var(--color-text-muted)' }}>
              Showing locations for {selectedCustomer.name}
            </p>
          )}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={setItemsPerPage}
        totalItems={filteredLocations.length}
      />

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
              <SortableTableHeader
                field="name"
                label="Name"
                currentSortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableTableHeader
                field="state.name"
                label="State"
                currentSortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableTableHeader
                field="zipCode"
                label="Zip Code"
                currentSortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <th className="text-left p-3" style={{ color: 'var(--color-text-secondary)' }}>Customer</th>
              <th className="text-right p-3" style={{ color: 'var(--color-text-secondary)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((loc) => (
              <tr key={loc.id} className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                <td className="p-3" style={{ color: 'var(--color-text-primary)' }}>{loc.name}</td>
                <td className="p-3" style={{ color: 'var(--color-text-secondary)' }}>{loc.state.name}</td>
                <td className="p-3" style={{ color: 'var(--color-text-secondary)' }}>{loc.zipCode}</td>
                <td className="p-3" style={{ color: 'var(--color-text-secondary)' }}>{loc.customer?.name || '-'}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end space-x-2">
                    {canChange(loc.customerId) && (
                    <button
                      onClick={() => location.handleEdit(loc)}
                      className="p-2 rounded hover:bg-opacity-10"
                      style={{ color: 'var(--color-accent)' }}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    )}
                    {canDelete(loc.customerId) && (
                    <button
                      onClick={() => location.handleDelete(loc.id)}
                      className="p-2 rounded hover:bg-opacity-10"
                      style={{ color: 'var(--color-error)' }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Location Form Modal */}
      <FormModal
        isOpen={location.showForm}
        onClose={location.handleCancel}
        title={location.editing ? 'Edit Location' : 'Add New Location'}
        subtitle={location.editing ? 'Update location information and notes' : 'Create a new location with details'}
        icon={MapPin}
        maxWidth="2xl"
      >
        <form onSubmit={location.handleSubmit} className="space-y-4">
              {/* Customer Field - Editable */}
              <FormSelect
                label="Customer"
                required
                value={location.form.customerId}
                onChange={(e) => location.setForm({ ...location.form, customerId: e.target.value })}
                options={customerOptions}
                placeholder="Select customer..."
                backgroundColor="primary"
              />

              <FormInput
                label="Name"
                required
                type="text"
                value={location.form.name}
                onChange={(e) => location.setForm({ ...location.form, name: e.target.value })}
                backgroundColor="primary"
              />
              
              <FormInput
                label="Physical Address"
                type="text"
                value={location.form.physicalAddress}
                onChange={(e) => location.setForm({ ...location.form, physicalAddress: e.target.value })}
                placeholder="Street address, building number, etc."
                backgroundColor="primary"
              />

              <FormInput
                label="City"
                type="text"
                value={location.form.city}
                onChange={(e) => location.setForm({ ...location.form, city: e.target.value })}
                placeholder="City name"
                backgroundColor="primary"
              />

              <FormSelect
                label="State"
                required
                value={location.form.stateId}
                onChange={(e) => location.setForm({ ...location.form, stateId: e.target.value })}
                options={stateOptions}
                placeholder="Select state..."
                backgroundColor="primary"
              />

              <FormInput
                label="Zip Code"
                type="text"
                value={location.form.zipCode}
                onChange={(e) => location.setForm({ ...location.form, zipCode: e.target.value })}
                backgroundColor="primary"
              />

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={location.handleCancel}
                  className="px-4 py-2 border rounded-lg transition-colors hover:bg-gray-50"
                  style={{
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-secondary)'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                  style={{
                    backgroundColor: 'var(--color-accent)',
                    color: 'var(--color-text-on-accent)'
                  }}
                  disabled={location.isCreating || location.isUpdating}
                >
                  {location.isCreating || location.isUpdating ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
      </FormModal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={location.showDeleteModal}
        onClose={location.handleCancelDelete}
        onConfirm={location.handleConfirmDelete}
        title="Delete Location"
        message="Are you sure you want to delete this location? This action cannot be undone."
        type="danger"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={location.isDeleting}
      />
    </div>
  );
};

